import React, { useMemo } from 'react';
import moment from "moment";
import { Container, Form, Button, Icon, Message, Segment, Dimmer, Loader, Dropdown } from 'semantic-ui-react';
import SemanticDatepicker from "react-semantic-ui-datepickers";
import TSReactTable from "../../frameworks/TSReactTable";
import useDropdownItem from "../../frameworks/hooks/useDropdownItem";
import { 
	TABLE_HEADER_STYLE,
	getColumnsList,
	dateToString,
} from "../../utils/Util";
import { URL_REPORT, URL_TRUCK } from '../../constances/urls';
import { GET } from '../../frameworks/HttpClient';


export default function TestFieldLatexReportView() {
	const [dateRange, setDateRange] = React.useState(null);
	const [dateRangeError, setDateRangeError] = React.useState(false);
	const [productOptions, setProductOptions] = React.useState([]);
	const [productFilter, setProductFilter] = React.useState("ALL");
	const [customerFilter, setCustomerFilter] = React.useState("ALL");
	const customerOptions = useDropdownItem({url: `${URL_TRUCK.TRUCK_CUSTOMER}?show_in_report=true`});
	const [total, setTotal] = React.useState(0);
	const [nh3Average, setNH3Average] = React.useState(0);
	const [vfaAverage, setVFAAverage] = React.useState(0);
	// Table
	const [data, setData] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [errorMessages, setErrorMessages] = React.useState(null);

	const customerDropdownOptions = useMemo(() => [
		{key: "ALL", value: "ALL", text: "ทั้งหมด"},
		...customerOptions.response,
	], [customerOptions.response]);

	const columns = useMemo(() => [
		...getColumnsList([
			{ 
				Header: "วันที่", 
				accessor: "created", 
				width: 100,
				Cell: ({value}) => (<p>{dateToString(value)}</p>)
			},
			{ Header: "เวลาเข้า", accessor: "in_datetime", width: 70 },
			{ Header: "เวลาออก", accessor: "out_datetime", width: 70 },
			{ Header: "เวลาแลป", accessor: "time", width: 70 },
			{ 
				Header: "จุดรับซื้อ", 
				accessor: "customer_name",
				style: {textAlign: "left"},
				Footer: "TOTAL", 
				getFooterProps: () => ({style: {...TABLE_HEADER_STYLE, textAlign: "center"}}),
			},
			{ Header: "ทะเบียน", accessor: "license_plate", width: 90 },
			{ Header: "สินค้า", accessor: "product_name", width: 130 },
			{ 
				Header: "น้ำหนัก", 
				accessor: "net_weight", 
				width: 80,
				Cell: ({value}) => (<p>{parseFloat(value).toLocaleString("en-US")}</p>),
				Footer: parseFloat(total).toLocaleString("en-US"),
				getFooterProps: () => ({style: {...TABLE_HEADER_STYLE, textAlign: "center"}}),
			},
			{ 
				Header: "NH3", 
				accessor: "nh3", 
				width: 60,
				Footer: parseFloat(nh3Average).toFixed(2),
				getFooterProps: () => ({style: {...TABLE_HEADER_STYLE, textAlign: "center"}}),
			},
			{ 
				Header: "VFA", 
				accessor: "vfa", 
				width: 60,
				Footer: parseFloat(vfaAverage).toFixed(3),
				getFooterProps: () => ({style: {...TABLE_HEADER_STYLE, textAlign: "center"}}),
			},
			{ Header: "BULKING TANK NO.", accessor: "bulking_tank_no", width: 90 },
			{ Header: "Note", accessor: "note", width: 70 },
			{ Header: "REMARK", accessor: "is_approved", width: 80 },
			{ Header: "CHECK BY", accessor: "inspector", width: 120 }
		]),
	])

	const fetchData = async () => {
		
		if (!dateRange) {
			setDateRangeError(true)
			return;
		}

		setDateRangeError(false);	
		setLoading(true)
		setErrorMessages(null)
		const params = {
			date_before: moment(dateRange[0]).format('YYYY-MM-DD'),
			date_after: moment(dateRange[1]).format('YYYY-MM-DD'),
			product: productFilter,
			customer: customerFilter,
		}
		try {
			const response = await GET(URL_REPORT.TRUCK_TRANSACTION_QUALITY_CHECK_REPORT, params)
			let totalWeight = response.data.reduce((c, p) => c + Number(p.net_weight), Number(0));
			let totalNH3 = response.data.reduce((c, p) => c + (Number(p.nh3) * Number(p.net_weight)), Number(0));
			let totalVFA = response.data.reduce((c, p) => c + (Number(p.vfa) * Number(p.net_weight)), Number(0));
			setTotal(totalWeight)
			setNH3Average((totalWeight > 0) ? (totalNH3 / totalWeight).toFixed(2) : 0)
			setVFAAverage((totalWeight > 0) ? (totalVFA / totalWeight).toFixed(3) : 0)
			setData(response.data)
		} catch (error) {
			setErrorMessages(error.errorMessages)
		} finally {
			setLoading(false)
		}
	}

	const fetchProductOptions = async () => {
		try {
			const response = await GET(URL_TRUCK.TRUCK_PRODUCT, {allowed_weight_in: true});
			const options = response.data.map(item => {
				return {key: item.id, value: item.id, text: item.name}
			})
			setProductOptions([
				{key: "ALL", value: "ALL", text: "ทั้งหมด"},
				...options,
			]);
		} catch (error) {
			setErrorMessages(error.errorMessages)
		}
	}

	const gotoExport = (exportType) => {
		if (!dateRange) {
			setDateRangeError(true)
			return;
		}
		const dateBefore = moment(dateRange[0]).format('YYYY-MM-DD');
		const dateAfter = moment(dateRange[1]).format('YYYY-MM-DD');
		const params = `date_before=${dateBefore}&date_after=${dateAfter}&product=${productFilter}&customer=${customerFilter}`
		window.open(`${URL_REPORT.TRUCK_TRANSACTION_QUALITY_CHECK_REPORT_EXPORT}${exportType}/?${params}`);
	}

	React.useEffect(() => {
		fetchProductOptions();
		customerOptions.fetchData();
	}, [])

	return (
		<Container fluid style={{marginTop: 24}}>
			<Form>
				<Form.Group>
					<Form.Field
						label={"ช่วงวันที่"}
						control={SemanticDatepicker}
						locale="en-US"
						type="range"
						showToday={true}
						format="DD/MM/YY"
						placeholder={"เลือกช่วงเวลา..."}
						value={dateRange}
						error={dateRangeError}
						onChange={(_, data) => setDateRange(data.value)}/>
					<Form.Field 
						label={"สินค้า"}
						control={Dropdown}
						selectOnBlur={false}
						selection
						options={productOptions}
						value={productFilter}
						onChange={(_, data) => setProductFilter(data.value)}/>
					<Form.Field 
						label={"จุดรับซื้อ"}
						control={Dropdown}
						selectOnBlur={false}
						selection
						search={true}
						options={customerDropdownOptions}
						value={customerFilter}
						onChange={(_, data) => setCustomerFilter(data.value)}/>
					<div style={{marginTop: "auto"}}>
						<Button primary onClick={() => fetchData()}>ค้นหา</Button>
					</div>
				</Form.Group>
			</Form>

			{ errorMessages && 
				<Message error>{errorMessages}</Message>
			}
			<Segment basic style={{padding: 0}}>
				<Dimmer active={loading} inverted><Loader inverted/></Dimmer>
				<TSReactTable
					columns={columns}
					data={data}
					pageSize={(data.length > 0) ? data.length : 10}
					showPagination={false}/>

				<Button 
					style={{marginTop: 8}}
					color={"red"}
					floated={"right"}
					size="mini"
					disabled={!dateRange}
					onClick={() => gotoExport("pdf")}>
						<Icon name={"file pdf"}/>
						PDF
				</Button>

				<Button
					style={{marginTop: 8, marginRight: 8}}
					color={"green"}
					floated={"right"}
					size="mini"
					disabled={!dateRange}
					onClick={() => gotoExport("excel")}>
						<Icon name={"file excel"}/>
						Excel
				</Button>
			</Segment>
		</Container>
	)
}
